<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from 'vue'
import { useVoiceStore } from '@/stores/voice'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { logError } from '@/composables/useLogger'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Icon } from '#components'

const emit = defineEmits(['close'])

const voiceStore = useVoiceStore()

// UI state
const showChatHistory = ref(false)
const showMetadata = ref(true)

// Computed properties for reactive state
const isActive = computed(() => voiceStore.isActive)
const isConnecting = computed(() => voiceStore.isConnecting)
const error = computed(() => voiceStore.error)
const needsUserInteraction = computed(() => voiceStore.needsUserInteraction)
const audioLevel = computed(() => voiceStore.audioLevel)
const chatHistory = computed(() => voiceStore.chatHistory)
const interimUserTranscript = computed(() => voiceStore.interimUserTranscript)
const interimAgentTranscript = computed(() => voiceStore.interimAgentTranscript)
const agentAudioLevel = computed(() => voiceStore.agentAudioLevel)

// WebRTC & Context Metadata
const connectionStatus = computed(() => {
  if (!voiceStore.room) return 'Disconnected'
  return voiceStore.room.state || 'Unknown'
})

const contextMetadata = computed(() => {
  return {
    currentRoute: voiceStore.currentRoute || 'Unknown',
    focusedItems: voiceStore.getCurrentFocusedItems?.() || [],
    sessionStart: voiceStore.sessionStartTime,
    lastContextSent: voiceStore.lastContextSent,
    recentViews: voiceStore.recentViews || [],
  }
})

// Real-time context data being prepared for next update
const liveContextData = computed(() => {
  if (!isActive.value) return null

  const focusUuids = voiceStore.getCurrentFocusedItems?.() || []
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/'
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  return {
    userTimezone: userTimezone,
    currentView: {
      platform: 'web',
      route: currentPath,
      params: {},
      previousViews: voiceStore.recentViews || [],
    },
    focusUuids: focusUuids.length > 0 ? focusUuids : undefined,
    sessionMetadata: {
      sessionStart: voiceStore.sessionStartTime,
      timestamp: new Date().toISOString(),
      voiceChatActive: true,
      audioLevel: audioLevel.value,
      agentAudioLevel: agentAudioLevel.value,
      listening: voiceStore.isListening,
    },
  }
})

// Check if context has changed since last send
const contextHasChanged = computed(() => {
  if (!liveContextData.value || !contextMetadata.value.lastContextSent) return true

  const current = JSON.stringify(liveContextData.value)
  const lastSent = JSON.stringify(contextMetadata.value.lastContextSent)

  return current !== lastSent
})

// Next update timing
const nextUpdateIn = ref(0)
let updateTimer: ReturnType<typeof setInterval> | null = null

const startUpdateTimer = () => {
  if (updateTimer) clearInterval(updateTimer)
  nextUpdateIn.value = 60

  updateTimer = setInterval(() => {
    if (nextUpdateIn.value > 0) {
      nextUpdateIn.value--
    } else {
      nextUpdateIn.value = 60 // Reset for next cycle
    }
  }, 1000)
}

const stopUpdateTimer = () => {
  if (updateTimer) {
    clearInterval(updateTimer)
    updateTimer = null
  }
  nextUpdateIn.value = 0
}

// Watch for voice chat state changes
watch(
  isActive,
  (active) => {
    if (active) {
      startUpdateTimer()
    } else {
      stopUpdateTimer()
    }
  },
  { immediate: true }
)

// Reset timer when context is sent (watch for changes in lastContextSent)
watch(
  () => contextMetadata.value.lastContextSent,
  (newValue, oldValue) => {
    if (newValue && newValue !== oldValue && isActive.value) {
      // Context was just sent, reset timer
      nextUpdateIn.value = 60
    }
  },
  { deep: true }
)

const roomMetadata = computed(() => {
  if (!voiceStore.room) return null
  return {
    name: voiceStore.room.name || 'Unknown',
    localParticipant: voiceStore.room.localParticipant?.identity || 'Unknown',
    numParticipants: voiceStore.room.numParticipants || 0,
    state: voiceStore.room.state || 'Unknown',
  }
})

const lastServerAction = computed(() => voiceStore.lastServerAction)

// Ref for the container where we'll put the debug audio element
const chatContainer = ref<InstanceType<typeof ScrollArea> | null>(null)

// Computed properties for visualizers with smoothing to prevent flickering
const isUserSpeaking = computed(() => isActive.value && audioLevel.value > 10)
const isAiSpeaking = computed(() => isActive.value && agentAudioLevel.value > 20)

// Start or stop the voice chat
const toggleChat = async () => {
  if (isActive.value) {
    await voiceStore.endChat()
  } else {
    await voiceStore.startChat()
  }
}

// Function to play a test sound
const playTestSound = () => {
  const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)() // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!audioCtx) {
    logError('Web Audio API is not supported in this browser')
    alert('Cannot play test sound: Web Audio API not supported.')
    return
  }
  const oscillator = audioCtx.createOscillator()
  const gainNode = audioCtx.createGain()

  oscillator.type = 'sine' // Simple sine wave
  oscillator.frequency.setValueAtTime(440, audioCtx.currentTime) // A4 note
  gainNode.gain.setValueAtTime(0.5, audioCtx.currentTime) // Reduce volume slightly
  gainNode.gain.exponentialRampToValueAtTime(0.0001, audioCtx.currentTime + 0.5)

  oscillator.connect(gainNode)
  gainNode.connect(audioCtx.destination)

  oscillator.start(audioCtx.currentTime)
  oscillator.stop(audioCtx.currentTime + 0.5)

  oscillator.onended = () => {
    audioCtx.close().catch((e) => logError('Error closing AudioContext', e))
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    const scrollAreaEl = chatContainer.value?.$el
    const scrollableViewport = scrollAreaEl?.querySelector('[data-radix-scroll-area-viewport]')
    if (scrollableViewport) {
      scrollableViewport.scrollTop = scrollableViewport.scrollHeight
    }
  })
}

watch(
  () => [chatHistory.value.length, interimUserTranscript.value, interimAgentTranscript.value],
  () => {
    scrollToBottom()
  },
  { deep: true }
)

// Cleanup timer on unmount
onUnmounted(() => {
  stopUpdateTimer()
})
</script>

<template>
  <div class="w-full max-w-2xl mx-auto flex flex-col h-full">
    <!-- Header -->
    <div class="flex flex-row items-center justify-between p-4 border-b">
      <h2 class="text-lg font-semibold">Voice Chat</h2>
      <div class="flex items-center space-x-2">
        <Button variant="outline" size="sm" @click="playTestSound">Test Sound</Button>
        <Button variant="ghost" size="icon" class="h-8 w-8" @click="emit('close')">
          <Icon name="heroicons:x-mark" class="h-5 w-5" />
        </Button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="p-4 space-y-3 flex-grow overflow-auto">
      <!-- Error Display -->
      <Alert v-if="error" variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{{ error }}</AlertDescription>
      </Alert>

      <!-- Audio Permission Alert -->
      <Alert v-if="needsUserInteraction" variant="default">
        <AlertTitle>Audio Permission Required</AlertTitle>
        <AlertDescription>
          Please click "Start Voice Chat" to grant microphone access.
        </AlertDescription>
      </Alert>

      <!-- Control Button -->
      <div class="flex justify-center">
        <Button
          size="lg"
          :variant="isActive ? 'destructive' : 'default'"
          :disabled="isConnecting"
          @click="toggleChat"
        >
          <span v-if="isConnecting">Connecting...</span>
          <span v-else>{{ isActive ? 'End Chat' : 'Start Voice Chat' }}</span>
        </Button>
      </div>

      <!-- Audio Visualizers -->
      <div v-if="isActive" class="space-y-3">
        <!-- User Input Visualizer -->
        <div class="relative">
          <div class="text-xs font-medium text-muted-foreground mb-1 flex items-center">
            <Icon name="heroicons:microphone" class="w-3 h-3 mr-1" />
            Your Voice
            <Badge v-if="isUserSpeaking" variant="secondary" class="ml-2 text-xs">Speaking</Badge>
          </div>
          <div
            class="h-12 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-lg border border-blue-200 dark:border-blue-800 overflow-hidden relative"
          >
            <!-- Waveform bars -->
            <div class="flex items-end justify-center h-full gap-1 p-2">
              <div
                v-for="i in 24"
                :key="`user-${i}`"
                class="bg-gradient-to-t from-blue-500 to-blue-400 rounded-sm transition-all duration-150 ease-out"
                :class="{ 'animate-pulse': isUserSpeaking }"
                :style="{
                  width: '2px',
                  height: `${Math.max(4, Math.min(100, audioLevel * (0.5 + Math.sin(i * 0.5) * 0.3) + Math.random() * 8))}%`,
                  animationDelay: `${i * 20}ms`,
                }"
              />
            </div>
            <!-- Glow effect when speaking -->
            <div
              v-if="isUserSpeaking"
              class="absolute inset-0 bg-blue-400/20 animate-pulse rounded-lg"
            />
          </div>
        </div>

        <!-- AI Speaking Visualizer -->
        <div class="relative">
          <div class="text-xs font-medium text-muted-foreground mb-1 flex items-center">
            <Icon name="heroicons:sparkles" class="w-3 h-3 mr-1" />
            AI Response
            <Badge v-if="isAiSpeaking" variant="secondary" class="ml-2 text-xs">Speaking</Badge>
          </div>
          <div
            class="h-12 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 rounded-lg border border-purple-200 dark:border-purple-800 overflow-hidden relative"
          >
            <!-- AI Waveform bars -->
            <div class="flex items-end justify-center h-full gap-1 p-2">
              <div
                v-for="i in 24"
                :key="`ai-${i}`"
                class="bg-gradient-to-t from-purple-500 to-pink-400 rounded-sm transition-all duration-150 ease-out"
                :class="{ 'animate-pulse': isAiSpeaking }"
                :style="{
                  width: '2px',
                  height: `${Math.max(4, Math.min(100, agentAudioLevel * (0.5 + Math.sin(i * 0.4) * 0.3) + Math.random() * 8))}%`,
                  animationDelay: `${i * 20}ms`,
                }"
              />
            </div>
            <!-- Glow effect when speaking -->
            <div
              v-if="isAiSpeaking"
              class="absolute inset-0 bg-purple-400/20 animate-pulse rounded-lg"
            />
          </div>
        </div>
      </div>

      <!-- WebRTC & Context Metadata -->
      <Collapsible v-if="isActive" v-model:open="showMetadata">
        <CollapsibleTrigger class="w-full">
          <Card class="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardHeader class="py-2 px-3">
              <CardTitle class="text-sm flex items-center justify-between">
                <div class="flex items-center">
                  <Icon name="heroicons:signal" class="w-4 h-4 mr-2" />
                  WebRTC Metadata
                  <Badge v-if="nextUpdateIn > 0" variant="secondary" class="ml-2 text-xs">
                    Next update: {{ nextUpdateIn }}s
                  </Badge>
                </div>
                <Icon
                  :name="showMetadata ? 'heroicons:chevron-up' : 'heroicons:chevron-down'"
                  class="w-4 h-4"
                />
              </CardTitle>
            </CardHeader>
          </Card>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Card>
            <CardContent class="pt-3 pb-2 px-3 space-y-2">
              <!-- Connection Status -->
              <div class="flex items-center justify-between text-xs">
                <span class="text-muted-foreground">Connection:</span>
                <Badge :variant="connectionStatus === 'connected' ? 'default' : 'destructive'">
                  {{ connectionStatus }}
                </Badge>
              </div>

              <!-- Room Info -->
              <div v-if="roomMetadata" class="space-y-1">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Room:</span>
                  <span class="font-mono text-xs">{{ roomMetadata.name }}</span>
                </div>
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Participants:</span>
                  <span>{{ roomMetadata.numParticipants }}</span>
                </div>
              </div>

              <!-- Current Context -->
              <div class="border-t pt-2 space-y-1">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Current Route:</span>
                  <span class="font-mono text-xs">{{ contextMetadata.currentRoute }}</span>
                </div>
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Focus Items:</span>
                  <span>{{ contextMetadata.focusedItems.length }}</span>
                </div>
                <div v-if="contextMetadata.focusedItems.length > 0" class="text-xs">
                  <div class="text-muted-foreground mb-1">UUIDs:</div>
                  <div class="font-mono text-xs space-y-1">
                    <div
                      v-for="uuid in contextMetadata.focusedItems.slice(0, 2)"
                      :key="uuid"
                      class="bg-muted rounded px-2 py-1"
                    >
                      {{ uuid.substring(0, 8) }}...
                    </div>
                  </div>
                </div>
                <div
                  v-if="contextMetadata.lastContextSent"
                  class="flex items-center justify-between text-xs"
                >
                  <span class="text-muted-foreground">Last Update:</span>
                  <span class="text-xs">
                    {{
                      new Date(
                        contextMetadata.lastContextSent.sessionMetadata?.timestamp || ''
                      ).toLocaleTimeString()
                    }}
                  </span>
                </div>
              </div>

              <!-- Live Context Data -->
              <div v-if="liveContextData" class="border-t pt-2 space-y-1">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Live Context:</span>
                  <Badge :variant="contextHasChanged ? 'default' : 'secondary'" class="text-xs">
                    <Icon
                      :name="
                        contextHasChanged
                          ? 'heroicons:exclamation-triangle'
                          : 'heroicons:check-circle'
                      "
                      class="w-3 h-3 mr-1"
                      :class="{ 'animate-pulse': contextHasChanged }"
                    />
                    {{ contextHasChanged ? 'Changed' : 'Unchanged' }}
                  </Badge>
                </div>
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Audio Levels:</span>
                  <span class="text-xs">U:{{ audioLevel }} / A:{{ agentAudioLevel }}</span>
                </div>
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Focus Items:</span>
                  <span class="text-xs">{{ (liveContextData.focusUuids || []).length }}</span>
                </div>
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Next Update:</span>
                  <span class="text-xs font-mono">{{ nextUpdateIn }}s</span>
                </div>
                <div class="text-xs">
                  <div class="text-muted-foreground mb-1">Live Data Preview:</div>
                  <div
                    class="bg-muted rounded px-2 py-1 font-mono text-xs max-h-20 overflow-y-auto"
                    :class="{ 'border-l-2 border-yellow-500': contextHasChanged }"
                  >
                    {{ JSON.stringify(liveContextData, null, 2) }}
                  </div>
                </div>
              </div>

              <!-- Last Server Action -->
              <div v-if="lastServerAction" class="border-t pt-2 space-y-1">
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Last Server Action:</span>
                  <Badge variant="outline" class="text-xs">{{ lastServerAction.type }}</Badge>
                </div>
                <div class="flex items-center justify-between text-xs">
                  <span class="text-muted-foreground">Received:</span>
                  <span class="text-xs">
                    {{ new Date(lastServerAction.timestamp).toLocaleTimeString() }}
                  </span>
                </div>
                <div v-if="lastServerAction.data" class="text-xs">
                  <div class="text-muted-foreground mb-1">Data:</div>
                  <div
                    class="bg-muted rounded px-2 py-1 font-mono text-xs max-h-16 overflow-y-auto"
                  >
                    {{ JSON.stringify(lastServerAction.data, null, 2) }}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      <!-- Chat History (Collapsible) -->
      <Collapsible v-if="isActive" v-model:open="showChatHistory">
        <CollapsibleTrigger class="w-full">
          <Card class="cursor-pointer hover:bg-muted/50 transition-colors">
            <CardHeader class="py-2 px-3">
              <CardTitle class="text-sm flex items-center justify-between">
                <div class="flex items-center">
                  <Icon name="heroicons:chat-bubble-left-right" class="w-4 h-4 mr-2" />
                  Chat History ({{ chatHistory.length }})
                </div>
                <Icon
                  :name="showChatHistory ? 'heroicons:chevron-up' : 'heroicons:chevron-down'"
                  class="w-4 h-4"
                />
              </CardTitle>
            </CardHeader>
          </Card>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ScrollArea ref="chatContainer" class="h-48 w-full rounded-md border p-3">
            <div class="space-y-2">
              <div v-for="message in chatHistory" :key="message.id">
                <div v-if="message.type === 'user'" class="bg-muted p-2 rounded-lg rounded-bl-none">
                  <p class="text-xs font-semibold text-muted-foreground">You</p>
                  <p class="mt-1 text-sm">{{ message.text }}</p>
                </div>
                <div v-else class="bg-primary/10 p-2 rounded-lg rounded-br-none">
                  <p class="text-xs font-semibold text-primary">Agent</p>
                  <p class="mt-1 text-sm">{{ message.text }}</p>
                </div>
              </div>

              <!-- Interim transcripts -->
              <div v-if="interimUserTranscript" class="opacity-60">
                <div class="bg-muted p-2 rounded-lg rounded-bl-none">
                  <p class="text-xs font-semibold text-muted-foreground">You</p>
                  <p class="mt-1 text-sm italic">{{ interimUserTranscript }}</p>
                </div>
              </div>
              <div v-if="interimAgentTranscript" class="opacity-60">
                <div class="bg-primary/10 p-2 rounded-lg rounded-br-none">
                  <p class="text-xs font-semibold text-primary">Agent</p>
                  <p class="mt-1 text-sm italic">{{ interimAgentTranscript }}</p>
                </div>
              </div>
            </div>
          </ScrollArea>
        </CollapsibleContent>
      </Collapsible>
    </div>
  </div>
</template>

<style scoped>
/* Visualizer animations and effects are handled via Tailwind classes */
</style>
